/**
 * Global refresh mode (Manual / Auto) and manual refresh signal for data tabs.
 * Implementation plan: D3.4–D3.5c, D4.4 (pipeline interval).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type RefreshMode = 'manual' | 'auto';

const STORAGE_KEY = 'observability-dashboard-refresh-mode';

/** Services + Alerts auto-poll interval (D3.5a). */
export const POLL_INTERVAL_SERVICES_ALERTS_MS = 60_000;

/** Pipeline auto-poll interval (D4.4). */
export const POLL_INTERVAL_PIPELINE_MS = 90_000;

/** Costs tab auto-poll — cost data updates roughly daily (Stage 7 D7.15). */
export const POLL_INTERVAL_COSTS_MS = 300_000;

function readStoredMode(): RefreshMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'auto' || v === 'manual') {
      return v;
    }
  } catch {
    /* ignore quota / private mode */
  }
  return 'manual';
}

export interface RefreshContextValue {
  mode: RefreshMode;
  setMode: (mode: RefreshMode) => void;
  manualRefreshNonce: number;
  requestManualRefresh: () => void;
  tabVisible: boolean;
}

const RefreshContext = createContext<RefreshContextValue | null>(null);

export function RefreshProvider({ children }: { children: ReactNode }): JSX.Element {
  const [mode, setModeState] = useState<RefreshMode>(() =>
    typeof window !== 'undefined' ? readStoredMode() : 'manual',
  );
  const [manualRefreshNonce, setManualRefreshNonce] = useState(0);
  const [tabVisible, setTabVisible] = useState(() => {
    if (typeof document === 'undefined') {
      return true;
    }
    // jsdom / some test envs omit `visibilityState`; treat as visible so tabs still fetch.
    return document.visibilityState !== 'hidden';
  });

  const setMode = useCallback((next: RefreshMode) => {
    setModeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, []);

  const requestManualRefresh = useCallback(() => {
    setManualRefreshNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const onVisibility = (): void => {
      setTabVisible(document.visibilityState !== 'hidden');
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const value = useMemo(
    (): RefreshContextValue => ({
      mode,
      setMode,
      manualRefreshNonce,
      requestManualRefresh,
      tabVisible,
    }),
    [mode, setMode, manualRefreshNonce, requestManualRefresh, tabVisible],
  );

  return <RefreshContext.Provider value={value}>{children}</RefreshContext.Provider>;
}

export function useRefresh(): RefreshContextValue {
  const ctx = useContext(RefreshContext);
  if (ctx === null) {
    throw new Error('useRefresh must be used within RefreshProvider');
  }
  return ctx;
}
